import * as tslib_1 from "tslib";
import { MappingMode } from "@awayjs/graphics";
import { ShaderChunkBase } from "./ShaderChunkBase";
/**
 * DiffuseBasicChunk provides the default shading method for Lambert (dot3) diffuse lighting.
 */
var DiffuseBasicChunk = (function (_super) {
    tslib_1.__extends(DiffuseBasicChunk, _super);
    /**
     * Creates a new DiffuseBasicChunk object.
     */
    function DiffuseBasicChunk(method, shader) {
        var _this = _super.call(this, method, shader) || this;
        _this._ambientColorR = 1;
        _this._ambientColorG = 1;
        _this._ambientColorB = 1;
        _this._color = 0xffffff;
        _this._colorR = 1;
        _this._colorG = 1;
        _this._colorB = 1;
        _this._method = method;
        _this._shader = shader;
        return _this;
    }
    /**
     *
     */
    DiffuseBasicChunk.prototype.onInvalidate = function (event) {
        _super.prototype.onInvalidate.call(this, event);
        this._updateProperties();
    };
    DiffuseBasicChunk.prototype._isUsed = function () {
        return Boolean(this._shader.numLights);
    };
    DiffuseBasicChunk.prototype._initVO = function (chunkVO) {
        if (this._method.texture) {
            this._texture = this._shader.getAbstraction(this._method.texture);
            if (this._method.texture.mappingMode == MappingMode.CUBE)
                chunkVO.needsNormals = true;
            else
                this._shader.uvDependencies++;
        }
        else if (this._texture) {
            this._texture = null;
        }
        if (this._shader.numLights) {
            this._shader.usesCommonData = true;
            chunkVO.needsNormals = true;
        }
    };
    /**
     * @inheritDoc
     */
    DiffuseBasicChunk.prototype._initConstants = function () {
        this._updateProperties();
    };
    /**
     * @inheritDoc
     */
    DiffuseBasicChunk.prototype._cleanCompilationData = function () {
        _super.prototype._cleanCompilationData.call(this);
        this._totalLightColorReg = null;
    };
    /**
     * @inheritDoc
     */
    DiffuseBasicChunk.prototype._getFragmentPreLightingCode = function (registerCache, sharedRegisters) {
        var code = "";
        this._pIsFirstLight = true;
        registerCache.addFragmentTempUsages(this._totalLightColorReg = registerCache.getFreeFragmentVectorTemp(), 1);
        return code;
    };
    /**
     * @inheritDoc
     */
    DiffuseBasicChunk.prototype._getFragmentCodePerLight = function (lightDirReg, lightColReg, registerCache, sharedRegisters) {
        var code = "";
        var t;
        // write in temporary if not first light, so we can add to total diffuse colour
        if (this._pIsFirstLight) {
            t = this._totalLightColorReg;
        }
        else {
            t = registerCache.getFreeFragmentVectorTemp();
            registerCache.addFragmentTempUsages(t, 1);
        }
        code += "dp3 " + t + ".x, " + lightDirReg + ", " + sharedRegisters.normalFragment + "\n" +
            "max " + t + ".w, " + t + ".x, " + sharedRegisters.commons + ".y\n";
        if (this._shader.usesLightFallOff)
            code += "mul " + t + ".w, " + t + ".w, " + lightDirReg + ".w\n";
        if (this._modulateFunction != null)
            code += this._modulateFunction(t, registerCache, sharedRegisters);
        code += "mul " + t + ", " + t + ".w, " + lightColReg + "\n";
        if (!this._pIsFirstLight) {
            code += "add " + this._totalLightColorReg + ".xyz, " + this._totalLightColorReg + ", " + t + "\n";
            registerCache.removeFragmentTempUsage(t);
        }
        this._pIsFirstLight = false;
        return code;
    };
    /**
     * @inheritDoc
     */
    DiffuseBasicChunk.prototype._getFragmentCodePerProbe = function (cubeMapReg, weightRegister, registerCache, sharedRegisters) {
        var code = "";
        var t;
        // write in temporary if not first light, so we can add to total diffuse colour
        if (this._pIsFirstLight) {
            t = this._totalLightColorReg;
        }
        else {
            t = registerCache.getFreeFragmentVectorTemp();
            registerCache.addFragmentTempUsages(t, 1);
        }
        code += "tex " + t + ", " + sharedRegisters.normalFragment + ", " + cubeMapReg + " <cube,linear,miplinear>\n" +
            "mul " + t + ".xyz, " + t + ".xyz, " + weightRegister + "\n";
        if (this._modulateFunction != null)
            code += this._modulateFunction(t, registerCache, sharedRegisters);
        if (!this._pIsFirstLight) {
            code += "add " + this._totalLightColorReg + ".xyz, " + this._totalLightColorReg + ", " + t + "\n";
            registerCache.removeFragmentTempUsage(t);
        }
        this._pIsFirstLight = false;
        return code;
    };
    /**
     * @inheritDoc
     */
    DiffuseBasicChunk.prototype._getFragmentCode = function (targetReg, registerCache, sharedRegisters) {
        var code = "";
        var diffuseColor;
        var cutOffReg;
        // incorporate input from ambient
        if (sharedRegisters.shadowTarget)
            code += this._applyShadow(registerCache, sharedRegisters);
        registerCache.addFragmentTempUsages(diffuseColor = registerCache.getFreeFragmentVectorTemp(), 1);
        var ambientColorRegister = registerCache.getFreeFragmentConstant();
        this._ambientColorRegister = ambientColorRegister.index * 4;
        if (this._texture) {
            code += this._texture._getFragmentCode(diffuseColor, registerCache, sharedRegisters, (this._method.texture.mappingMode == MappingMode.CUBE) ? sharedRegisters.normalFragment : sharedRegisters.uvVarying);
        }
        else {
            var diffuseColorRegister = registerCache.getFreeFragmentConstant();
            this._diffuseColorRegister = diffuseColorRegister.index * 4;
            code += "mov " + diffuseColor + ", " + diffuseColorRegister + "\n";
        }
        code += "sat " + this._totalLightColorReg + ", " + this._totalLightColorReg + "\n" +
            "mul " + diffuseColor + ".xyz, " + diffuseColor + ", " + this._totalLightColorReg + "\n";
        if (this._method.multiply) {
            code += "add " + diffuseColor + ".xyz, " + diffuseColor + ", " + ambientColorRegister + "\n" +
                "mul " + targetReg + ".xyz, " + targetReg + ", " + diffuseColor + "\n";
        }
        else if (this._texture) {
            code += "mul " + targetReg + ".xyz, " + targetReg + ", " + ambientColorRegister + "\n" +
                "mul " + this._totalLightColorReg + ".xyz, " + targetReg + ", " + this._totalLightColorReg + "\n" +
                "sub " + targetReg + ".xyz, " + targetReg + ", " + this._totalLightColorReg + "\n" +
                "add " + targetReg + ".xyz, " + targetReg + ", " + diffuseColor + "\n"; //add diffuse color and ambient color
        }
        else {
            code += "mul " + this._totalLightColorReg + ".xyz, " + ambientColorRegister + ", " + this._totalLightColorReg + "\n" +
                "sub " + this._totalLightColorReg + ".xyz, " + ambientColorRegister + ", " + this._totalLightColorReg + "\n" +
                "add " + diffuseColor + ".xyz, " + diffuseColor + ", " + this._totalLightColorReg + "\n" +
                "mul " + targetReg + ".xyz, " + targetReg + ", " + diffuseColor + "\n"; // multiply by target which could be texture or white
        }
        registerCache.removeFragmentTempUsage(this._totalLightColorReg);
        registerCache.removeFragmentTempUsage(diffuseColor);
        return code;
    };
    /**
     * Generate the code that applies the calculated shadow to the diffuse light
     * @param methodVO The MethodVO object for which the compilation is currently happening.
     * @param regCache The register cache the compiler is currently using for the register management.
     */
    DiffuseBasicChunk.prototype._applyShadow = function (regCache, sharedRegisters) {
        return "mul " + this._totalLightColorReg + ".xyz, " + this._totalLightColorReg + ", " + sharedRegisters.shadowTarget + ".w\n";
    };
    /**
     * @inheritDoc
     */
    DiffuseBasicChunk.prototype._activate = function () {
        if (this._texture) {
            this._texture.activate();
        }
        else {
            var index = this._diffuseColorRegister;
            var data = this._shader.fragmentConstantData;
            if (this._method.multiply) {
                data[index] = this._colorR * this._ambientColorR;
                data[index + 1] = this._colorG * this._ambientColorG;
                data[index + 2] = this._colorB * this._ambientColorB;
            }
            else {
                data[index] = this._colorR;
                data[index + 1] = this._colorG;
                data[index + 2] = this._colorB;
            }
            data[index + 3] = 1;
        }
    };
    /**
     * Updates the diffuse and ambient color data used by the render state.
     */
    DiffuseBasicChunk.prototype._updateProperties = function () {
        this._ambientColor = this._shader.pass.style.color;
        this._ambientColorR = ((this._ambientColor >> 16) & 0xff) / 0xff;
        this._ambientColorG = ((this._ambientColor >> 8) & 0xff) / 0xff;
        this._ambientColorB = (this._ambientColor & 0xff) / 0xff;
        this._color = this._method.color;
        this._colorR = ((this._color >> 16) & 0xff) / 0xff;
        this._colorG = ((this._color >> 8) & 0xff) / 0xff;
        this._colorB = (this._color & 0xff) / 0xff;
    };
    /**
     * @inheritDoc
     */
    DiffuseBasicChunk.prototype._setRenderState = function (renderable, projection) {
        if (this._texture)
            this._texture._setRenderState(renderable);
        //TODO move this to Activate (ambientR/G/B currently calc'd in render state)
        var index = this._ambientColorRegister;
        var data = this._shader.fragmentConstantData;
        data[index] = this._shader.ambientR * this._ambientColorR;
        data[index + 1] = this._shader.ambientG * this._ambientColorG;
        data[index + 2] = this._shader.ambientB * this._ambientColorB;
        data[index + 3] = 1;
    };
    return DiffuseBasicChunk;
}(ShaderChunkBase));
export { DiffuseBasicChunk };



// WEBPACK FOOTER //
// ../dist/lib/chunks/DiffuseBasicChunk.js